/**
 * The tables standing in the maze's dead ends.
 *
 * One kinematic body each, and that is the load-bearing decision (design D3). A dynamic
 * table would be the obvious choice for something that can be pushed around, and it is
 * the wrong one here: the player's character controller has
 * `setApplyImpulsesToDynamicBodies(true)`, so a heavy dynamic table jitters when leaned
 * on, sinks when stood on, and drifts away from a player who merely brushes past. Kinematic
 * gives the three things the change actually asks for — unshovable, stable to stand on,
 * and moved only by the drag controller — with none of that.
 *
 * Colliders are compound: a slab for the top and four legs, so a player can crouch-walk
 * nothing but can see daylight under it and a thrown box can roll beneath. `userData` is
 * what the interaction ray reads to offer "Drag".
 *
 * Ids are derived from placement order (`table-0`, `table-1`, …). `placeTables` is seeded
 * and deterministic, so the same seed gives the same table the same id on every launch —
 * which is what lets the store name one and the registry find it.
 */

import { CuboidCollider, RigidBody, type RapierRigidBody } from '@react-three/rapier';
import { useEffect, useRef } from 'react';
import { BackSide } from 'three';
import type { Table } from '../game/maze/placeTables';
import { useGameStore } from '../store/gameStore';
import { getGradientMap } from './materials';
import { registerObjectBody, unregisterObjectBody } from './objectRegistry';
import type { BodyUserData } from './useInteractionTarget';

/** Old outdoor furniture: darker and redder than the ladders, so the two read apart. */
const TABLE_COLOR = '#6f4b2a';

/** Thickness of the top slab. */
const TOP_THICKNESS = 0.08;

/** Square section of a leg. */
const LEG_THICKNESS = 0.09;

/** How far a leg is inset from the corner of the top. */
const LEG_INSET = 0.1;

/** Placement order is seeded and stable, so index is a stable identity. */
const tableId = (index: number) => `table-${index}`;

type Props = {
  tables: readonly Table[];
};

export function MazeTables({ tables }: Props) {
  return (
    <>
      {tables.map((table, index) => (
        <MazeTable key={tableId(index)} id={tableId(index)} table={table} />
      ))}
    </>
  );
}

function MazeTable({ id, table }: { id: string; table: Table }) {
  const bodyRef = useRef<RapierRigidBody>(null);
  const isFocused = useGameStore((store) => store.focus?.id === id);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    registerObjectBody(id, body);
    return () => unregisterObjectBody(id);
  }, [id]);

  const { size, topHeight } = table;
  const legHeight = topHeight - TOP_THICKNESS;
  const legX = size.x / 2 - LEG_INSET - LEG_THICKNESS / 2;
  const legZ = size.z / 2 - LEG_INSET - LEG_THICKNESS / 2;
  const legs: [number, number][] = [
    [-legX, -legZ],
    [legX, -legZ],
    [-legX, legZ],
    [legX, legZ],
  ];

  return (
    <RigidBody
      ref={bodyRef}
      // Positioned rather than velocity-based: the drag controller moves it by setting
      // where it should be, the same way `CarryController` moves a carried box.
      type="kinematicPosition"
      colliders={false}
      position={[table.center.x, 0, table.center.z]}
      rotation={[0, table.yaw, 0]}
      userData={{ kind: 'table', id } satisfies BodyUserData}
    >
      <CuboidCollider
        args={[size.x / 2, TOP_THICKNESS / 2, size.z / 2]}
        position={[0, topHeight - TOP_THICKNESS / 2, 0]}
      />
      {legs.map(([x, z], index) => (
        <CuboidCollider
          key={`leg-collider-${index}`}
          args={[LEG_THICKNESS / 2, legHeight / 2, LEG_THICKNESS / 2]}
          position={[x, legHeight / 2, z]}
        />
      ))}

      <mesh position={[0, topHeight - TOP_THICKNESS / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[size.x, TOP_THICKNESS, size.z]} />
        <meshToonMaterial
          color={TABLE_COLOR}
          gradientMap={getGradientMap()}
          // Focus feedback is brightness plus the outline and the prompt — never hue alone.
          emissive={isFocused ? TABLE_COLOR : '#000000'}
          emissiveIntensity={isFocused ? 0.35 : 0}
        />
      </mesh>
      {legs.map(([x, z], index) => (
        <mesh key={`leg-${index}`} position={[x, legHeight / 2, z]} castShadow receiveShadow>
          <boxGeometry args={[LEG_THICKNESS, legHeight, LEG_THICKNESS]} />
          <meshToonMaterial color={TABLE_COLOR} gradientMap={getGradientMap()} />
        </mesh>
      ))}

      {isFocused && (
        <mesh position={[0, topHeight - TOP_THICKNESS / 2, 0]} scale={1.04}>
          <boxGeometry args={[size.x, TOP_THICKNESS, size.z]} />
          <meshBasicMaterial color="#ffffff" side={BackSide} />
        </mesh>
      )}
    </RigidBody>
  );
}
